import type { ShopNavSubitem, ShopPage } from '@/services/shopWebsiteService'
import { normalizeSectionAnchorId } from '@/utils/shopAnchors'

export function publicPathForShopPage(
  basePath: string,
  page: Pick<ShopPage, 'page_type' | 'slug'>
): string {
  if (page.page_type === 'home' || page.slug === 'home') return basePath
  return `${basePath}/${page.slug}`
}

export type StorefrontNavChild = { to: string; label: string }

export type StorefrontNavItem = {
  to: string
  label: string
  children?: StorefrontNavChild[]
}

export function storefrontNavPageLabel(page: Pick<ShopPage, 'title' | 'nav_label'>): string {
  const short = String(page.nav_label ?? '').trim()
  return short || page.title
}

export function navItemsFromPublishedPages(pages: ShopPage[], basePath: string): StorefrontNavItem[] {
  return [...pages]
    .filter((p) => p.show_in_nav !== false)
    .sort((a, b) => a.sort_order - b.sort_order || a.page_id - b.page_id)
    .map((p) => {
      const to = publicPathForShopPage(basePath, p)
      const navLabel = storefrontNavPageLabel(p)
      const raw = p.nav_subitems
      const list: unknown[] = Array.isArray(raw) ? raw : []
      const children: StorefrontNavChild[] = []
      for (const row of list) {
        if (!row || typeof row !== 'object') continue
        const item = row as ShopNavSubitem
        const label = String(item.label ?? '').trim()
        if (!label) continue
        const pathRaw = String(item.path ?? '').trim()
        const pathSeg = pathRaw
          ? normalizeSectionAnchorId(pathRaw)
          : ''
        const anchor = normalizeSectionAnchorId(String(item.anchor ?? ''))
        if (pathSeg && anchor) continue
        if (pathSeg) {
          children.push({ label, to: `${basePath}/${pathSeg}` })
        } else if (anchor) {
          children.push({ label, to: `${to}#${anchor}` })
        }
      }
      if (children.length > 0) {
        return { to, label: navLabel, children }
      }
      return { to, label: navLabel }
    })
}

/** Footer: parent link then subsection links (same page #anchors). */
export function flattenNavForFooter(items: StorefrontNavItem[]): { to: string; label: string }[] {
  const out: { to: string; label: string }[] = []
  for (const item of items) {
    if (item.children?.length) {
      out.push({ to: item.to, label: item.label })
      for (const c of item.children) {
        out.push({ to: c.to, label: c.label })
      }
    } else {
      out.push({ to: item.to, label: item.label })
    }
  }
  return out
}
